import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    Link as MuiLink,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    TextField,
    Tooltip,
    Pagination,
    Typography
} from '@mui/material';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import JSZip from 'jszip';
import { buildSearchIndex, matchQuery } from '../utils/closingPriceSearch';

const DEFAULT_SEARCH_PLACEHOLDERS = {
    0: 'Search year folder (e.g. 2026)',
    1: 'Search month folder (e.g. 03 or March)',
    2: 'Search filename (e.g. 01.03.2026.csv)',
    default: 'Search filename or date'
};

const DEFAULT_LEVEL_LABELS = {
    0: 'Year',
    1: 'Month',
    2: 'Day',
    default: 'Folder'
};

const ACCENT_COLORS = {
    textPrimary: '#245372',
    textSecondary: '#5B7284',
    textMuted: '#7B8D9B',
    chipActiveBg: '#CBF0E6',
    chipInactiveBg: '#F2FBF8',
    chipActiveText: '#245372',
    chipInactiveText: '#3E5567',
    accent: '#3DC3C1',
    accentSoft: '#ECFAF5',
    headerBg: '#CBF0E6'
};

const toMediaUrl = (url = '') => url.replace(/^\/static/, '/media');

const formatBytes = (bytes = 0) => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const index = Math.min(
        Math.floor(Math.log(bytes) / Math.log(1024)),
        units.length - 1
    );
    const value = bytes / 1024 ** index;
    return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[index]}`;
};

const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString();
};

const ClosingPriceExplorer = ({
    title,
    description,
    rootPath,
    breadcrumbRootLabel,
    searchPlaceholders,
    levelLabels,
    onFileOpen
}) => {
    const PAGE_SIZE = 35;
    const rootClean = useMemo(() => rootPath.replace(/\/+$/, ''), [rootPath]);

    const [currentPath, setCurrentPath] = useState(rootPath);
    const [items, setItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedFiles, setSelectedFiles] = useState({});
    const [downloadFolderName, setDownloadFolderName] =
        useState('closing-prices');
    const [zipping, setZipping] = useState(false);
    const [zipError, setZipError] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [downloadingDateRange, setDownloadingDateRange] = useState(false);

    const mergedPlaceholders = useMemo(
        () => ({ ...DEFAULT_SEARCH_PLACEHOLDERS, ...searchPlaceholders }),
        [searchPlaceholders]
    );

    const mergedLevelLabels = useMemo(
        () => ({ ...DEFAULT_LEVEL_LABELS, ...levelLabels }),
        [levelLabels]
    );

    const loadDirectory = useCallback(async (path) => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch(path, { credentials: 'include' });
            if (!response.ok) {
                throw new Error(`Request failed (${response.status})`);
            }
            const data = await response.json();
            setItems(Array.isArray(data?.files) ? data.files : []);
            if (data?.path) {
                setCurrentPath(toMediaUrl(data.path));
            }
        } catch (e) {
            setError(e?.message || 'Failed to load closing-price directory');
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDirectory(currentPath);
    }, [currentPath, loadDirectory]);

    useEffect(() => {
        setSearchTerm('');
        setPage(1);
        setSelectedFiles({});
        setZipError('');
    }, [currentPath]);

    useEffect(() => {
        setPage(1);
    }, [searchTerm]);

    const sortedItems = useMemo(() => {
        return [...items].sort((a, b) => {
            if (a.is_dir !== b.is_dir) return a.is_dir ? -1 : 1;
            return (a.name || '').localeCompare(b.name || '', undefined, {
                numeric: true,
                sensitivity: 'base'
            });
        });
    }, [items]);

    const filteredItems = useMemo(() => {
        const query = searchTerm.trim();
        if (!query) return sortedItems;

        return sortedItems.filter((item) =>
            matchQuery(buildSearchIndex(item, formatDate), query)
        );
    }, [sortedItems, searchTerm]);

    const totalPages = useMemo(() => {
        return Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
    }, [filteredItems.length]);

    const paginatedItems = useMemo(() => {
        const startIndex = (page - 1) * PAGE_SIZE;
        return filteredItems.slice(startIndex, startIndex + PAGE_SIZE);
    }, [filteredItems, page]);

    const selectableFiles = useMemo(() => {
        return filteredItems
            .filter((item) => !item.is_dir)
            .map((item) => ({
                key: item.url || item.name,
                url: toMediaUrl(item.url || ''),
                name: item.name || 'closing-price.csv'
            }));
    }, [filteredItems]);

    const selectedCount = useMemo(
        () => Object.keys(selectedFiles).length,
        [selectedFiles]
    );

    const allSelectableSelected = useMemo(() => {
        if (selectableFiles.length === 0) return false;
        return selectableFiles.every((file) =>
            Boolean(selectedFiles[file.url])
        );
    }, [selectableFiles, selectedFiles]);

    const hasSubdirectories = useMemo(
        () => items.some((item) => item.is_dir),
        [items]
    );

    const hasFilesInCurrentDirectory = useMemo(
        () => items.some((item) => !item.is_dir),
        [items]
    );

    const showFolderZipControls =
        !loading && !error && hasFilesInCurrentDirectory && !hasSubdirectories;

    const relativePath = useMemo(() => {
        const clean = currentPath.replace(/\/+$/, '');
        return clean.replace(rootClean, '').replace(/^\/+/, '');
    }, [currentPath, rootClean]);

    const segments = useMemo(
        () => (relativePath ? relativePath.split('/') : []),
        [relativePath]
    );

    const searchPlaceholder = useMemo(() => {
        return (
            mergedPlaceholders[segments.length] || mergedPlaceholders.default
        );
    }, [mergedPlaceholders, segments]);

    const breadcrumbParts = useMemo(() => {
        const parts = [{ label: breadcrumbRootLabel, path: rootPath }];
        let acc = rootClean;
        segments.forEach((seg) => {
            acc = `${acc}/${seg}`;
            parts.push({ label: seg, path: acc });
        });
        return parts;
    }, [breadcrumbRootLabel, rootPath, rootClean, segments]);

    const directoryLevelLabel = useMemo(() => {
        return mergedLevelLabels[segments.length] || mergedLevelLabels.default;
    }, [mergedLevelLabels, segments]);

    const canGoUp = segments.length > 0;

    const handleUp = () => {
        if (!canGoUp) return;
        const parent = segments.slice(0, -1).join('/');
        setCurrentPath(parent ? `${rootClean}/${parent}` : rootPath);
    };

    const handleToggleFileSelection = (item, mediaUrl) => {
        if (item.is_dir) return;
        setSelectedFiles((prev) => {
            const next = { ...prev };
            if (next[mediaUrl]) {
                delete next[mediaUrl];
            } else {
                next[mediaUrl] = item.name || 'closing-price.csv';
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        if (allSelectableSelected) {
            setSelectedFiles({});
            return;
        }
        const nextSelected = {};
        selectableFiles.forEach((file) => {
            nextSelected[file.url] = file.name;
        });
        setSelectedFiles(nextSelected);
    };

    const triggerFileDownload = (url, fileName) => {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadSelected = () => {
        const filesToDownload = Object.entries(selectedFiles);
        filesToDownload.forEach(([url, fileName], index) => {
            setTimeout(() => {
                triggerFileDownload(url, fileName);
            }, index * 180);
        });
    };

    const sanitizeFolderName = (name) => {
        const cleaned = (name || '').trim().replace(/[\\/:*?"<>|]/g, '-');
        return cleaned || 'closing-prices';
    };

    const getUniqueName = (fileName, used) => {
        if (!used.has(fileName)) {
            used.add(fileName);
            return fileName;
        }

        const extIndex = fileName.lastIndexOf('.');
        const hasExt = extIndex > 0;
        const base = hasExt ? fileName.slice(0, extIndex) : fileName;
        const ext = hasExt ? fileName.slice(extIndex) : '';

        let counter = 1;
        let nextName = `${base} (${counter})${ext}`;
        while (used.has(nextName)) {
            counter += 1;
            nextName = `${base} (${counter})${ext}`;
        }
        used.add(nextName);
        return nextName;
    };

    const handleDownloadSelectedAsFolder = async () => {
        const filesToDownload = Object.entries(selectedFiles);
        if (filesToDownload.length === 0 || zipping) return;

        setZipping(true);
        setZipError('');
        try {
            const folderName = sanitizeFolderName(downloadFolderName);
            const zip = new JSZip();
            const zipFolder = zip.folder(folderName);
            const usedNames = new Set();

            for (const [url, fileName] of filesToDownload) {
                const resp = await fetch(url, { credentials: 'include' });
                if (!resp.ok) {
                    throw new Error(`Failed to download ${fileName}`);
                }
                const blob = await resp.blob();
                const uniqueName = getUniqueName(fileName, usedNames);
                zipFolder.file(uniqueName, blob);
            }

            const zipBlob = await zip.generateAsync({ type: 'blob' });
            const zipUrl = URL.createObjectURL(zipBlob);
            triggerFileDownload(zipUrl, `${folderName}.zip`);
            setTimeout(() => URL.revokeObjectURL(zipUrl), 2000);
        } catch (e) {
            setZipError(e?.message || 'Failed to build ZIP file');
        } finally {
            setZipping(false);
        }
    };

    const handleDownloadDateRange = async () => {
        if (!startDate || !endDate) return;

        setDownloadingDateRange(true);
        try {
            const formattedStartDate = dayjs(startDate).format('MM-DD-YYYY');
            const formattedEndDate = dayjs(endDate).format('MM-DD-YYYY');

            const apiEndpoint = `/api/getcsv?from=${encodeURIComponent(formattedStartDate)}&to=${encodeURIComponent(formattedEndDate)}`;

            const response = await fetch(apiEndpoint, {
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to download date range data');
            }

            const blob = await response.blob();
            const fileName = `closing-prices-${formattedStartDate}-to-${formattedEndDate}.csv`;
            const url = URL.createObjectURL(blob);
            triggerFileDownload(url, fileName);
            setTimeout(() => URL.revokeObjectURL(url), 2000);
        } catch (e) {
            setZipError(e?.message || 'Failed to download date range');
        } finally {
            setDownloadingDateRange(false);
        }
    };

    return (
        <Box
            sx={{
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 0 6px rgba(0,0,0,0.1)',
                overflow: 'hidden'
            }}
        >
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid #E7EEF5'
                }}
            >
                <Typography
                    sx={{
                        fontSize: '1.15rem',
                        fontWeight: 700,
                        color: ACCENT_COLORS.textPrimary
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{
                        fontSize: '0.85rem',
                        color: ACCENT_COLORS.textSecondary
                    }}
                >
                    {description}
                </Typography>
                <Typography
                    sx={{
                        fontSize: '0.78rem',
                        color: ACCENT_COLORS.textSecondary,
                        mt: 0.75,
                        whiteSpace: 'pre-line'
                    }}
                >
                    {`Closing prices get exported as "csv" files. You can easily reformat and save them as "xlsx" files performing the following steps:

1. Mark column A and go to "Text in columns"
2. Select "Separated", click on "Next"
3. Select "Tabstopp" and "Comma", click on "Next"
4. Select "Standard" and click on "Complete"
5. Add "1000" in cell F1
6. Add in column D "Bid new" and E "Ask new" each the formulas B[+row no.]/$F$1 and C[+row no.]/$F$1 (e.g. B2/$F$1) to get the prices with a comma and 3 decimal places
7. Save the reformatted csv file as xlsx file.`}
                </Typography>
            </Box>

            {/* <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                sx={{
                    px: 2,
                    py: 1.25,
                    backgroundColor: ACCENT_COLORS.headerBg,
                    borderTop: '2px solid #3DC3C1'
                }}
            > */}
            {/* <Breadcrumbs
                    separator={<NavigateNextRoundedIcon fontSize="small" />}
                >
                    {breadcrumbParts.map((crumb, idx) => (
                        <Chip
                            key={crumb.path}
                            size="small"
                            clickable={idx !== breadcrumbParts.length - 1}
                            label={crumb.label}
                            onClick={
                                idx !== breadcrumbParts.length - 1
                                    ? () => setCurrentPath(crumb.path)
                                    : undefined
                            }
                            sx={{
                                fontWeight:
                                    idx === breadcrumbParts.length - 1
                                        ? 700
                                        : 500,
                                backgroundColor:
                                    idx === breadcrumbParts.length - 1
                                        ? ACCENT_COLORS.chipActiveBg
                                        : ACCENT_COLORS.chipInactiveBg,
                                color:
                                    idx === breadcrumbParts.length - 1
                                        ? ACCENT_COLORS.chipActiveText
                                        : ACCENT_COLORS.chipInactiveText
                            }}
                        />
                    ))}
                </Breadcrumbs> */}

            {/* <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleUp}
                        disabled={!canGoUp || loading}
                        sx={{
                            textTransform: 'none',
                            color: ACCENT_COLORS.textPrimary,
                            borderColor: ACCENT_COLORS.accent,
                            '&:hover': {
                                borderColor: ACCENT_COLORS.accent,
                                backgroundColor: ACCENT_COLORS.accentSoft
                            }
                        }}
                    >
                        Up One Level
                    </Button>
                </Stack> */}
            {/* </Stack> */}

            <Stack
                direction={{ xs: 'column', lg: 'row' }}
                sx={{
                    px: 2,
                    pb: 1.25,
                    pt: 2,
                    borderBottom: '2px solid #3DC3C1',
                    backgroundColor: ACCENT_COLORS.headerBg
                }}
            >
                {/* Left side - Search and file controls */}
                <Box sx={{ pr: { lg: 2 }, flex: 1 }}>
                    <Breadcrumbs
                        separator={<NavigateNextRoundedIcon fontSize="small" />}
                    >
                        {breadcrumbParts.map((crumb, idx) => (
                            <Chip
                                key={crumb.path}
                                size="small"
                                clickable={idx !== breadcrumbParts.length - 1}
                                label={crumb.label}
                                onClick={
                                    idx !== breadcrumbParts.length - 1
                                        ? () => setCurrentPath(crumb.path)
                                        : undefined
                                }
                                sx={{
                                    fontWeight:
                                        idx === breadcrumbParts.length - 1
                                            ? 700
                                            : 500,
                                    backgroundColor:
                                        idx === breadcrumbParts.length - 1
                                            ? ACCENT_COLORS.chipActiveBg
                                            : ACCENT_COLORS.chipInactiveBg,
                                    color:
                                        idx === breadcrumbParts.length - 1
                                            ? ACCENT_COLORS.chipActiveText
                                            : ACCENT_COLORS.chipInactiveText
                                }}
                            />
                        ))}
                    </Breadcrumbs>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{
                            maxWidth: '100% !important',
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: '1px solid #3DC3C1'
                            },
                            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                                {
                                    border: '1px solid #3DC3C1'
                                },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                                {
                                    border: '1px solid #3DC3C1'
                                },
                            backgroundColor: 'white'
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRoundedIcon
                                        sx={{ color: ACCENT_COLORS.textMuted }}
                                    />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        aria-label="clear search"
                                        onClick={() => setSearchTerm('')}
                                        edge="end"
                                    >
                                        <CloseRoundedIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : undefined
                        }}
                    />
                    <Typography
                        sx={{
                            fontSize: '0.75rem',
                            color: ACCENT_COLORS.textMuted,
                            mt: 0.75
                        }}
                    >
                        Showing {paginatedItems.length} of{' '}
                        {filteredItems.length} filtered items (
                        {sortedItems.length} total)
                    </Typography>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        sx={{ mt: 1 }}
                    >
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleSelectAll}
                            disabled={selectableFiles.length === 0}
                            sx={{ textTransform: 'none' }}
                        >
                            {allSelectableSelected
                                ? 'Clear all'
                                : 'Select all files'}
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<DownloadRoundedIcon />}
                            onClick={handleDownloadSelected}
                            disabled={selectedCount === 0}
                            className="page-button"
                            sx={{ textTransform: 'none' }}
                        >
                            Download Selected ({selectedCount})
                        </Button>
                        {showFolderZipControls && (
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                alignItems={{ xs: 'stretch', sm: 'center' }}
                                sx={{ ml: { sm: 'auto' } }}
                            >
                                <TextField
                                    size="small"
                                    label="Folder name"
                                    value={downloadFolderName}
                                    onChange={(e) =>
                                        setDownloadFolderName(e.target.value)
                                    }
                                    placeholder="closing-prices"
                                    sx={{ minWidth: { xs: '100%', sm: 240 } }}
                                />
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handleDownloadSelectedAsFolder}
                                    disabled={selectedCount === 0 || zipping}
                                    className="page-button"
                                    sx={{ textTransform: 'none' }}
                                >
                                    {zipping
                                        ? 'Preparing ZIP...'
                                        : 'Download Folder ZIP'}
                                </Button>
                            </Stack>
                        )}
                    </Stack>
                    {zipError && (
                        <Typography
                            sx={{
                                fontSize: '0.75rem',
                                color: '#D32F2F',
                                mt: 0.75
                            }}
                        >
                            {zipError}
                        </Typography>
                    )}
                </Box>

                {/* Right side - Date Range Download */}
                {/* <Box
                    sx={{
                        // minWidth: { xs: '100%', lg: 'auto' },
                        borderLeft: { lg: '2px solid #3DC3C1' },
                        borderTop: { xs: '2px solid #3DC3C1', lg: 'none' },
                        pl: { lg: 2 },
                        pt: { xs: 2, lg: 0 }
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: ACCENT_COLORS.textPrimary,
                            mb: 1
                        }}
                    >
                        Download by Date Range
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Stack
                            direction={{ xs: 'column', lg: 'row' }}
                            spacing={1}
                            alignItems={{ lg: 'flex-start' }}
                        >
                            <DatePicker
                                label="Start Date"
                                format="DD/MM/YYYY"
                                value={startDate}
                                onChange={(value) => setStartDate(value)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        placeholder: 'DD/MM/YYYY'
                                    }
                                }}
                                sx={{
                                    backgroundColor: 'white'
                                }}
                            />
                            <DatePicker
                                label="End Date"
                                format="DD/MM/YYYY"
                                value={endDate}
                                onChange={(value) => setEndDate(value)}
                                minDate={startDate}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        placeholder: 'DD/MM/YYYY'
                                    }
                                }}
                                sx={{
                                    backgroundColor: 'white'
                                }}
                            />
                        </Stack>
                    </LocalizationProvider>
                    <Box sx={{ pt: 2 }}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<DownloadRoundedIcon />}
                            onClick={handleDownloadDateRange}
                            disabled={
                                !startDate || !endDate || downloadingDateRange
                            }
                            className="page-button"
                            sx={{
                                textTransform: 'none',
                                bgcolor: '#3DC3C1',
                                '&:hover': {
                                    bgcolor: '#2BAB99'
                                },
                                mt: 2
                            }}
                        >
                            {downloadingDateRange
                                ? 'Downloading...'
                                : 'Download CSV'}
                        </Button>
                    </Box>
                </Box> */}
            </Stack>

            <Divider />

            {loading ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <CircularProgress size={28} />
                </Box>
            ) : error ? (
                <Box sx={{ p: 2 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            ) : (
                <>
                    <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
                        <List sx={{ p: 1 }}>
                            {filteredItems.length === 0 && (
                                <Typography sx={{ p: 2, color: '#667D8E' }}>
                                    {searchTerm.trim()
                                        ? 'No matching files found for your search.'
                                        : 'No files found in this directory.'}
                                </Typography>
                            )}

                            {paginatedItems.map((item) => {
                                const mediaUrl = toMediaUrl(item.url || '');
                                const isSelected = Boolean(
                                    selectedFiles[mediaUrl]
                                );
                                const secondary = item.is_dir
                                    ? `${directoryLevelLabel} ${item.name}`
                                    : `${formatBytes(item.size)} • ${formatDate(item.mod_time)}`;

                                return (
                                    <ListItemButton
                                        key={item.url || item.name}
                                        onClick={() => {
                                            if (item.is_dir) {
                                                setCurrentPath(mediaUrl);
                                            } else if (onFileOpen) {
                                                onFileOpen(item, mediaUrl);
                                            } else {
                                                window.open(
                                                    mediaUrl,
                                                    '_blank',
                                                    'noopener,noreferrer'
                                                );
                                            }
                                        }}
                                        sx={{
                                            borderRadius: '8px',
                                            mb: 0.5,
                                            backgroundColor: isSelected
                                                ? '#ECFAF5'
                                                : 'transparent',
                                            '&:hover': {
                                                backgroundColor:
                                                    ACCENT_COLORS.accentSoft
                                            }
                                        }}
                                    >
                                        {!item.is_dir && (
                                            <Checkbox
                                                size="small"
                                                checked={isSelected}
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                onChange={() =>
                                                    handleToggleFileSelection(
                                                        item,
                                                        mediaUrl
                                                    )
                                                }
                                            />
                                        )}
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            {item.is_dir ? (
                                                <FolderRoundedIcon
                                                    sx={{
                                                        color: ACCENT_COLORS.accent
                                                    }}
                                                />
                                            ) : (
                                                <InsertDriveFileRoundedIcon
                                                    sx={{ color: '#6D7E8D' }}
                                                />
                                            )}
                                        </ListItemIcon>

                                        <ListItemText
                                            primary={item.name}
                                            secondary={secondary}
                                            primaryTypographyProps={{
                                                fontWeight: item.is_dir
                                                    ? 600
                                                    : 500,
                                                color: '#245372'
                                            }}
                                            secondaryTypographyProps={{
                                                fontSize: '0.78rem',
                                                color: '#7B8D9B'
                                            }}
                                        />

                                        {!item.is_dir && (
                                            <Tooltip title="Download CSV">
                                                <MuiLink
                                                    href={mediaUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    underline="none"
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                >
                                                    <Button
                                                        size="small"
                                                        startIcon={
                                                            <DownloadRoundedIcon />
                                                        }
                                                        sx={{
                                                            textTransform:
                                                                'none'
                                                        }}
                                                    >
                                                        Download
                                                    </Button>
                                                </MuiLink>
                                            </Tooltip>
                                        )}
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Box>

                    {filteredItems.length > 0 && (
                        <Box
                            sx={{
                                px: 2,
                                pb: 1.5,
                                display: 'flex',
                                justifyContent: 'center'
                            }}
                        >
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, nextPage) => setPage(nextPage)}
                                size="small"
                                sx={{
                                    '& .MuiPaginationItem-root.Mui-selected': {
                                        backgroundColor: ACCENT_COLORS.accent,
                                        color: '#fff'
                                    },
                                    '& .MuiPaginationItem-root:hover': {
                                        backgroundColor:
                                            ACCENT_COLORS.accentSoft
                                    }
                                }}
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

ClosingPriceExplorer.defaultProps = {
    title: 'Closing Prices',
    description: 'Browse folders and download CSV files',
    rootPath: '/media/files/csv/closing/',
    breadcrumbRootLabel: 'closing',
    searchPlaceholders: DEFAULT_SEARCH_PLACEHOLDERS,
    levelLabels: DEFAULT_LEVEL_LABELS,
    onFileOpen: undefined
};

export default ClosingPriceExplorer;
